import { Component, Input, OnInit } from '@angular/core';
import { ModalController, ToastController } from '@ionic/angular';
import { BaseClass } from 'src/app/shared/classes/base';
import { SelectItem } from 'src/app/shared/classes/SelectItem';
import { QuestionConfig, QuestionGroup, QuestionGroupCategory, QuestionTypeConfig } from 'src/app/shared/interfaces/process-config.interface';
import { TranslationService } from 'src/app/shared/services/translation.service';
import * as uuid from "uuid";
import { Version } from '../../_interfaces/version.interface';
import { ProcessTypeEnum } from 'src/app/shared/interfaces/process.interface';
import { GoalTypeEnum } from 'src/app/shared/enums/GoalTypeEnum';
import { ConsentGoalsComponent } from 'src/app/shared/components/consent-goals/consent-goals.component';
import { QuestionGroupAttributesComponent } from './question-group/question-group-attributes/question-group-attributes.component';
import { ManageQuestionGroupComponent } from '../manage-question-group/manage-question-group.component';

@Component({
  selector: 'app-processes-questions-configuration',
  templateUrl: './processes-questions-configuration.component.html',
  styleUrls: ['./processes-questions-configuration.component.scss', '../manage-version.page.scss'],
})
export class ProcessesQuestionsConfigurationComponent extends BaseClass implements OnInit {

  @Input() version: Version = null;
  @Input() managerGroupsSelectItems: SelectItem[] = [];
  @Input() groupsSelectItems: SelectItem[] = [];

  public managerQuestionGroupsSelected: boolean = false;
  public nonConfigurableQuestionProcesses = [this.ProcessType.HeadEvaluation, this.ProcessType.GoalsReview, this.ProcessType.EvaluationCalibration];
  public managerQuestionGroupsAvailableProcesses = [this.ProcessType.ThreeSixtyEvaluation, this.ProcessType.ManagerEvaluation, this.ProcessType.SelfEvaluation];

  constructor(
    protected _toastController: ToastController,
    protected _translationService: TranslationService,
    private _modalController: ModalController,) {
    super(_toastController, _translationService);
  }

  ngOnInit() { }

  public managerQuestionGroupsAvailable(): boolean {
    return this.version && this.managerQuestionGroupsAvailableProcesses.includes(this.version.type);
  }

  public async addQuestionGroup(manager: boolean) {
    const modal = await this._modalController.create({
      component: ManageQuestionGroupComponent,
      cssClass: "view-goals-modal-style ruling",
      componentProps: {
        language: this.selectedLanguage,
        typeConfig: null,
        group: null,
      },
    });

    modal.onDidDismiss().then((event) => {
      if (event.data) {
        const config = event.data;
        const orderGroup =
          (manager
            ? this.version.managerQuestionGroups
              ? this.version.managerQuestionGroups.length
              : 0
            : this.version.questionGroups
              ? this.version.questionGroups.length
              : 0) + 1;
        const group = <QuestionGroup>{
          guid: uuid.v4(),
          category: manager ? QuestionGroupCategory.Manager : null,
          showOnCalibration: config.showOnCalibration,
          order: orderGroup,
          globalizationConfig: config.globalizationConfig,
          questions: [],
          attributes: [],
        };
        if (manager) {
          this.version.managerQuestionGroups.push(group);
          this.managerGroupsSelectItems.push({
            id: group.guid,
            name: group.globalizationConfig[this.selectedLanguage].title,
          });
        } else {
          if (!this.version.questionGroups) this.version.questionGroups = [];
          this.version.questionGroups.push(group);
          this.groupsSelectItems.push({
            id: group.guid,
            name: group.globalizationConfig[this.selectedLanguage].title,
          });
        }
      }
    });

    return await modal.present();
  }
  public async manageAttributes() {
    let emptyGroup: QuestionGroup;
    const modal = await this._modalController.create({
      component: QuestionGroupAttributesComponent,
      // cssClass: '',
      componentProps: {
        language: this.selectedLanguage,
        typeConfig: null,
        group: emptyGroup,
        version: this.version,
        consent: true,
      },
    });

    return await modal.present();
  }

  public async openGoalConsentModal() {
    if (this.version.goalType === GoalTypeEnum.Consent) {
      const modal = await this._modalController.create({
        component: ConsentGoalsComponent,
        cssClass: "bigger-modal",
        componentProps: {
          language: this.selectedLanguage,
          processId: this.version.id,
          processConfig: this.version,
        },
      });
      await modal.present();

      modal.onDidDismiss().then((dismissEvent) => {
        if (dismissEvent.data) {
          this.version.consentGoals = dismissEvent.data.consentGoals;
          this.version.consentGoalsGlobalizationConfig = dismissEvent.data.consentGoalsGlobalizationConfig;
        }
      });
    }
  }

  public async changeGoalType($event: any) {
    this.version.goalType = $event.detail.checked ? GoalTypeEnum.Consent : GoalTypeEnum.Input;
  }

  public reorderQuestions(questionId: string): void {
    const index = this.version.questions.findIndex((q) => q.guid === questionId);
    const currentQuestion = this.version.questions[index];

    this.version.questions.splice(index, 1);

    this.version.questions.splice(currentQuestion.order - 1, 0, currentQuestion);

    this.version.questions.forEach((question, qIndex) => (question.order = qIndex + 1));
  }

  public deleteQuestion(questionId: string): void {
    const index = this.version.questions.findIndex((q) => q.guid === questionId);
    this.version.questions.splice(index, 1);

    this.version.questions.forEach((question, qIndex) => (question.order = qIndex + 1));
  }
  // public ProcessType = ProcessTypeEnum;
  public updateQuestionTypeConfig(questionType: QuestionTypeConfig) {
    this.version.questions.forEach((q) => {
      if (q.typeConfig.id == questionType.id) {
        q.typeConfig = questionType;
      }
    });
  }
  public questionGroupsAvailable(): boolean {
    if (!this.version) return false;
    const type = this.version.type;
    return (
      type === ProcessTypeEnum.FreeEvaluationEndYear ||
      type === ProcessTypeEnum.FreeEvaluationMidYear ||
      type === ProcessTypeEnum.ThreeSixtyEvaluation ||
      type === ProcessTypeEnum.SelfEvaluation ||
      type === ProcessTypeEnum.ManagerEvaluation ||
      type === ProcessTypeEnum.SelfEvaluationMidYear ||
      type === ProcessTypeEnum.FinalFeedback ||
      type === ProcessTypeEnum.GenericProcess
    );
  }

  public groupFromQuestionChanged(config: any) {
    this._changeGroupFromQuestion(config.manager ? this.version.managerQuestionGroups : this.version.questionGroups, config.questionId, config.newGroupId);
  }

  public removeQuestionGroup(groupGuid: string) {
    if (this.version.questionGroups) {
      const index = this.version.questionGroups.findIndex((x) => x.guid === groupGuid);
      if (index >= 0) {
        this.version.questionGroups.splice(index, 1);
        this._initializeQuestionGroupsOrder(this.version.questionGroups);
        return;
      }
    }
    if (this.version.managerQuestionGroups) {
      const index = this.version.managerQuestionGroups.findIndex((x) => x.guid === groupGuid);
      if (index >= 0) {
        this.version.managerQuestionGroups.splice(index, 1);
        this._initializeQuestionGroupsOrder(this.version.questionGroups);
      }
    }
  }


  public moveUpGroup(groupId: string) {
    let index = this.version.questionGroups.findIndex((x) => x.guid === groupId);
    if (index >= 0) {
      this.swapGroups(this.version.questionGroups, index, index - 1);
    } else {
      index = this.version.managerQuestionGroups.findIndex((x) => x.guid === groupId);
      this.swapGroups(this.version.managerQuestionGroups, index, index - 1);
    }
  }

  public moveDownGroup(groupId: string) {
    let index = this.version.questionGroups.findIndex((x) => x.guid === groupId);
    if (index >= 0) {
      this.swapGroups(this.version.questionGroups, index, index + 1);
    } else {
      index = this.version.managerQuestionGroups.findIndex((x) => x.guid === groupId);
      this.swapGroups(this.version.managerQuestionGroups, index, index + 1);
    }
  }

  public swapGroups(groups: QuestionGroup[], index1: number, index2: number) {
    if (index1 !== index2 && index1 >= 0 && index1 < groups.length && index2 >= 0 && index2 < groups.length) {
      const temp = groups[index1];
      groups[index1] = groups[index2];
      groups[index2] = temp;
      this._initializeQuestionGroupsOrder(groups);
    }
  }

  public addQuestion(groupGuid?: string): void {
    const question: QuestionConfig = {
      globalizationConfig: {
        "pt-BR": {
          title: "",
          instructions: "",
          instructions_External: "",
          placeholder: "",
        },
        "en-US": {
          title: "",
          instructions: "",
          instructions_External: "",
          placeholder: "",
        },
        "es-ES": {
          title: "",
          instructions: "",
          instructions_External: "",
          placeholder: "",
        },
      },
      expanded: true,
      order: this.version.questions ? this.version.questions.length + 1 : null,
      required: false,
      showPreview: false,
      guid: uuid.v4(),
      groupGuid: groupGuid,
      formula: undefined,
      viewType: null,
      typeConfig: {
        name: "",
        type: undefined,
        max: undefined,
        possibleValues: [],
        possibleValuesGlobalized: {
          "pt-BR": [],
          "en-US": [],
          "es-ES": [],
        },
        colors: [],
      },
      attributes: [],
      relatedQuestions: [],
      attributesQuestionConfig: []
    };

    if (groupGuid) {
      let group = this.version.questionGroups.find((x) => x.guid === groupGuid);
      if (!group) {
        group = this.version.managerQuestionGroups.find((x) => x.guid === groupGuid);
      }
      if (group) {
        group.questions.push(question);
        question.order = group.questions.length;
      }
    } else {
      if (this.version.questions == null) this.version.questions = [];
      this.version.questions.push(question);
    }
  }

  private _initializeQuestionGroupsOrder(groups: QuestionGroup[]) {
    groups.forEach((g, index) => (g.order = index + 1));
  }

  private _changeGroupFromQuestion(groups: QuestionGroup[], questionId: string, newGroupGuid: string) {
    let question: QuestionConfig;

    for (const g of groups) {
      if (g.questions) {
        const index = g.questions.findIndex((x) => x.guid === questionId);
        if (index >= 0) {
          const removed = g.questions.splice(index, 1);
          if (removed.length > 0) {
            question = removed[0];
            this._initializeQuestionsOrderFromGroup(g.guid);
          }
        }
      }
    }

    if (question) {
      const group = groups.find((x) => x.guid === newGroupGuid);
      if (group) {
        question.groupGuid = group.guid;
        group.questions.push(question);
        question.order = group.questions.length;
      }
    }
  }

  private _initializeQuestionsOrderFromGroup(groupGuid: string) {
    const group = this.version.questionGroups.find((x) => x.guid === groupGuid);

    if (group) {
      group.questions.forEach((q, index) => (q.order = index + 1));
    }
  }
}
